/**
 * StaffRoleChip — Tier 0 module-local staff-role badge.
 *
 * Renders one of the two canonical `PlatformRole` values (SCRUM-131)
 * with role-specific copy + color family:
 *
 *   - `root`    → brand-tinted (privileged write tier)
 *   - `support` → neutral (read + reset-password)
 *
 * The display strings are sentence case ("Root" / "Support"). The chip
 * exists at every staff surface (list rows, detail page hero pill, Add
 * staff form preview).
 *
 * The role union is imported from
 * `apps/admin/src/shared/auth/admin-permissions.ts` (derived from
 * `@artemis/contracts`) so the type stays load-bearing with the
 * permission table (one source of truth for what roles exist).
 */

import * as React from 'react';
import { type PlatformRole } from '../../../shared/auth/admin-permissions.js';

export interface StaffRoleChipProps {
  readonly role: PlatformRole;
  readonly size?: 'sm' | 'md';
}

interface RoleVisual {
  readonly label: string;
  readonly background: string;
  readonly foreground: string;
  readonly border: string;
  /** Leading status-dot color (artboard Badge tone: brand/info/neutral). */
  readonly dot: string;
}

// Declared as a plain object literal (NOT a `Record<PlatformRole, …>`
// annotation) so dot access on a definite property — `VISUALS.root` /
// `VISUALS.support` — stays a definite `RoleVisual` under
// `noUncheckedIndexedAccess`. The `satisfies` clause still enforces
// exhaustive coverage of the 2-role `PlatformRole` union at compile time
// (add a role to the contract enum and this stops type-checking).
const VISUALS = {
  root: {
    label: 'Root',
    background:
      'color-mix(in oklab, var(--brand-500) 14%, var(--bg-surface))',
    foreground: 'var(--brand-700)',
    border: 'color-mix(in oklab, var(--brand-500) 32%, transparent)',
    dot: 'var(--brand-500)',
  },
  support: {
    label: 'Support',
    background: 'var(--bg-sunken)',
    foreground: 'var(--fg-secondary)',
    border: 'var(--border-default)',
    dot: 'var(--fg-tertiary)',
  },
} satisfies Record<PlatformRole, RoleVisual>;

export function StaffRoleChip({
  role,
  size = 'md',
}: StaffRoleChipProps): React.ReactElement {
  // Explicit branch over the exactly-two-member `PlatformRole` union
  // (`'root' | 'support'`, asserted exhaustive in admin-permissions.ts).
  // Dot access on the literal keeps `visual` a definite `RoleVisual` —
  // no index-widening to `| undefined`.
  const visual: RoleVisual = role === 'root' ? VISUALS.root : VISUALS.support;
  const fontSize = size === 'sm' ? '11px' : '12px';
  const padY = size === 'sm' ? '2px' : '3px';
  const padX = size === 'sm' ? '6px' : '8px';

  return (
    <span
      aria-label={`Staff role: ${visual.label}`}
      className="inline-flex items-center font-semibold"
      style={{
        gap: '6px',
        padding: `${padY} ${padX}`,
        fontSize,
        borderRadius: 'var(--radius-full)',
        background: visual.background,
        color: visual.foreground,
        border: `1px solid ${visual.border}`,
      }}
    >
      <span
        aria-hidden="true"
        className="shrink-0 rounded-full"
        style={{ width: '6px', height: '6px', background: visual.dot }}
      />
      {visual.label}
    </span>
  );
}
